<template>
  <div :class="`web ${hasFullWidthProductAds ? 'web--fullwidth' : ''}`">
    <EHeading id="web-heading" class="web__title" data-test-id="web-title">
      {{ $t('web.title') }}
    </EHeading>
    <EFullwidthProductAds
      v-if="hasFullWidthProductAds"
      class="web__fullwidth-product-ads"
      data-test-id="web-fullwidth-product-ads"
    />
    <EMainline :has-full-width-product-ads="hasFullWidthProductAds" class="web__mainline" />
    <ESidebar class="web__sidebar" />
    <ENoResults
      v-if="!hasResults"
      :query="query"
      :error="error"
      class="web__no-results"
      data-test-id="web-no-results"
    />
    <EMockingbirdControls v-if="mocks && mocks.length" custom-actions />
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import {
  MAIN_FOOTER_FAQ_CARDS,
  NEW_PERSONAL_COUNTER,
  PRIVACY_PROXY,
} from '~~/common/js/constants/feature-flags';
import firstSearchAfterInstall from '~~/common/vue/mixins/first-search-after-install';
import EHeading from '~~/common/vue/components/heading';
import EMockingbirdControls from '~~/mockingbird/client-nuxt/controls';
import ENoResults from '~~/search/common/vue/components/no-results';
import {
  GREEN_FEATURES,
  SATISFACTION_FEEDBACK_ENTITY,
  GREEN_RELATED_SEARCHES,
} from '~~/search/common/js/client/feature-flags';
import EMainline from '~/components/mainline';
import EFullwidthProductAds from '~/components/fullwidth-product-ads';
import ESidebar from '~/components/sidebar';
import {
  WEB_PRODUCT_ADS_FULLWIDTH_EXPERIMENT,
  SHOPPING_BING_EXPERIMENT,
  CALCULATOR_EXPERIMENT,
} from '~/constants';
import { getExperimentName } from '~/feature-flags';

export default {
  head() {
    return { meta: [this.goodallTag] };
  },
  components: {
    EHeading,
    EMainline,
    ESidebar,
    ENoResults,
    EMockingbirdControls,
    EFullwidthProductAds,
  },
  mixins: [firstSearchAfterInstall],
  // TODO: this method is getting a bit long, see if we can break it down
  async asyncData({ app, query, store }) {
    const { mainline, sidebar } = store.getters.results;
    const resultTypes = mainline.concat(sidebar).map(({ type }) => type);

    await app.$trackExperiments([
      CALCULATOR_EXPERIMENT,
      GREEN_FEATURES.flag,
      GREEN_RELATED_SEARCHES.flag,
      MAIN_FOOTER_FAQ_CARDS.flag,
      NEW_PERSONAL_COUNTER.flag,
      PRIVACY_PROXY.flag,
      SATISFACTION_FEEDBACK_ENTITY.flag,
      SHOPPING_BING_EXPERIMENT,
      WEB_PRODUCT_ADS_FULLWIDTH_EXPERIMENT,
      // automatically track experiments (feature flags) for all the result types
      ...resultTypes
        .filter((type) => type) // Avoid empty results with undefined type
        .map((type) => getExperimentName(type)),
    ]);
    // TODO: make it so overrideFromQuery and the meta tag
    // does not have to be set up for each app individually
    await store.dispatch('goodall/overrideFromQuery', query);
    return {
      goodallTag: app.$getGoodallMetaTag(),
    };
  },
  computed: {
    ...mapState('global', ['locale']),
    ...mapState('user', ['newTab']),
    ...mapState('mockingbird', ['mocks']),
    ...mapState([
      'error',
      'mainlineResults',
      'mainlineAdResultsCount',
      'sidebarResults',
    ]),
    ...mapGetters([
      'hasProductAdsResults',
      'hasMainlineResults',
      'hasSidebarResults',
      'dynamicPagination',
      'staticPagination',
    ]),
    query() {
      return this.$route.query.q?.toString();
    },
    hasResults() {
      return !this.error && (this.hasMainlineResults || this.hasSidebarResults);
    },
    hasFullWidthProductAds() {
      return this.hasProductAdsResults;
    },
  },
  mounted() {
    this.trackPageLoad();
  },
  methods: {
    ...mapActions(['trackPageLoad']),
  },
};
</script>

<style lang="scss" scoped>
$sidebar-width-desktop: 360px;
$mainline-sidebar-gap-desktop: $space-3l;
$right-gutter-max-width: 300px;

.web {
  display: contents;
  box-sizing: content-box;
  padding: $space-1s 0 0;
  font-size: $font-m;
  // font-family: $system-font-stack;

  @include desktop {
    display: grid;
    grid-template-areas: "left-gutter mainline mid-gutter sidebar right-gutter";
    grid-template-columns: $page-container-left-space-desktop
      minmax(0, 8fr)
      $mainline-sidebar-gap-desktop
      minmax(0, 4fr)
      $space-1l;
    padding: $space-m 0 $space-m;
  }

  @include desktop-l {
    grid-template-columns: $page-container-left-space-desktop
      minmax(0, $page-container-width-desktop)
      $mainline-sidebar-gap-desktop
      minmax(0, $sidebar-width-desktop)
      $space-1l;
  }

  &.web--fullwidth {
    @include desktop {
      grid-template-areas: "left-gutter experiment experiment experiment experiment"
        "left-gutter mainline mid-gutter sidebar right-gutter";
      grid-template-columns: $page-container-left-space-desktop
        minmax(0, 8fr)
        $mainline-sidebar-gap-desktop
        minmax(0, 4fr)
        minmax($space-1l, auto);
    }

    @include desktop-l {
      grid-template-columns: $page-container-left-space-desktop
        minmax(0, $page-container-width-desktop)
        $mainline-sidebar-gap-desktop
        minmax(0, $sidebar-width-desktop)
        minmax($space-1l, 1fr);
    }
  }
}

.web__mainline,
.web__no-results {
  grid-area: mainline;
}

.web__sidebar {
  display: none;
  grid-area: sidebar;

  @include desktop {
    display: block;
  }
}

.web__fullwidth-product-ads {
  grid-area: experiment;
}

.web__title {
  @extend %sr-only;
}
</style>
